import { createAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { serializeThunkApiError } from 'src/utils/helpers/rtkSerializeThunkApiError';
import { captureMessage } from '@sentry/browser';
import { t } from 'i18next';
import { LookupsSearchIndexEnum } from 'src/utils/enums/LookupsSearchIndexEnum';
import { getLang, invalidateApiResponseInCache, isProduction } from 'src/utils/helpers/utils';
import { resolveJobTitleName } from 'src/utils/jobs/jobTitleDisplay';
import { ApplicantListItem } from 'src/utils/interfaces/applicants/ApplicantListItem';
import { ApplicantsNavParams } from 'src/utils/interfaces/job/ApplicantsNavParams';
import axios from '../../config/axios';
import Toast from '../../shared/toast/Toast';
import { ENTERPRISE_JOB_POSTS_PAGE_LIMIT, FULLTIME_PARTNER_ID_KEY, FULLTIME_PARTNER_TOKEN_KEY, JOB_DETAILS_PATH, SAUDI_COUNTRY_ONLY_ID } from '../../utils/constants/constants';
import { JobStatusEnum } from '../../utils/enums/JobStatusEnum';
import { ResponseResult } from '../../utils/interfaces/http/ResponseResult';
import { FullTimeJob } from '../../utils/interfaces/job/Job';
import { JobFilterCounts, JobInitialState, JobsEnumObject, JobsFiltersInterface } from '../../utils/interfaces/job/Job.store';
import { FullTimeJobDetails, SaudInterviewConfigurationsJobUpsertRequest } from '../../utils/interfaces/job/JobDetails';
import { NewJobPost } from '../../utils/interfaces/job/NewJobPost';
import { GetJobListRequest, GetJobRequest, UnlockContactInfoRequest } from '../../utils/interfaces/job/QueryParams';
import { getPartnerInfo } from '../partner/partner';
import { TypesenseOptions } from 'src/utils/interfaces/job/TypesenseOptions';
import { getDefaultVettingFilters, getFiltersForVettingTab, getInvalidationParamsFilters, getListApplicantsApiUrl, getVettingApplicantsList, setIsBackendRecommending } from '../vetting/vetting';
import { postTypesenseMultiSearchLoadBalanced } from 'src/utils/helpers/typesenseMultiSearch';
import { PartnerStatus } from 'src/utils/enums/PartnerStatus';
import { VettingTabsEnum } from 'src/utils/enums/VettingTabsEnum';

const appKey = 'appFullTimeJobs'
const apiModuleUrl = 'pa/ft/job-posts/list-v2';
const jobDetailsUrl = 'pa/ft/job-posts';
const jobApplicationsUrl = 'pa/ft/job-applications';
const getProductsUrl = 'pa/ft/product/list';
const postJobUrl = 'pa/ft/job-posts/post-job';
const editJobUrl = 'pa/ft/job-posts/edit-job-post';
const jobTitleUrl = 'generic/ft/job-posts/search-job-titles';
const passPaymentPromptUrl = 'pa/ft/job-posts';
const closeJobUrl = 'pa/ft/job-posts/close-job-post/';
const reOpenJobUrl = 'pa/ft/job-posts/reopen-job-post/';
const archiveJobUrl = 'pa/ft/job-posts/archive/';
const recommendedSkillsUrl = 'pa/ft/job-posts/list-recommended-skills'
const writeJobDescriptionWithSaudUrl = 'pa/ft/job-posts/generate-ai-job-description';
const writeBulkMessageWithAiUrl = 'pa/ft/job-posts/generate-ai-bulk-message';
const validateJobDescriptionUrl = 'pa/ft/job-posts/validate-job-description';
const validateJobPostUrl = 'pa/ft/job-posts/validate-job-post';

export const getJobList = createAsyncThunk(`${appKey}/getJobList`, async (params: GetJobListRequest) => {
    const qParams = { ...params, isV2: true };
    if (!qParams?.skip) qParams.skip = 0;
    if (!qParams?.limit) qParams.limit = ENTERPRISE_JOB_POSTS_PAGE_LIMIT;
    if (qParams?.partnerId) delete qParams.partnerId;
    if (qParams?.isEnterprise) delete qParams.isEnterprise;
    if (qParams?.hideLoading) delete qParams.hideLoading;
    if (!qParams?.createdByMeOnly) delete qParams.createdByMeOnly;
    if (qParams?.isArchived) qParams.statuses = [JobStatusEnum.CLOSED];
    if (params?.isEnterprise && (!qParams?.statuses || qParams?.statuses?.length === 0)) {
        if (params.partnerStatus === PartnerStatus.UNDER_REVIEW || params.partnerStatus === PartnerStatus.REQUIRE_ADDITIONAL_INFO) {
            qParams.statuses = [JobStatusEnum.OPEN, JobStatusEnum.UNDER_REVIEW];
        } else {
            qParams.statuses = [JobStatusEnum.OPEN];
        }
    }
    if (qParams?.partnerStatus) delete qParams.partnerStatus;

    const response = await axios.get<ResponseResult<FullTimeJob[]>>(`${apiModuleUrl}`, { params: qParams });
    const res: any = response.data.data;
    return { list: res?.jobs || res, hideLoading: params.hideLoading, count: res?.count, activeCount: res?.activeCount, archivedCount: res?.archivedCount, closedCount: res?.closedCount };
})

export const getJobListFiltersCounts = createAsyncThunk(`${appKey}/getJobListFiltersCounts`, async (params: GetJobListRequest) => {
    const qParams = { ...params };
    if (!qParams?.skip) qParams.skip = 0;
    if (!qParams?.limit) qParams.limit = ENTERPRISE_JOB_POSTS_PAGE_LIMIT;
    if (qParams?.partnerId) delete qParams.partnerId;
    if (qParams?.isEnterprise) delete qParams.isEnterprise;
    if (qParams?.hideLoading) delete qParams.hideLoading;
    if (!qParams?.createdByMeOnly) delete qParams.createdByMeOnly;
    if (qParams?.isArchived) qParams.statuses = [JobStatusEnum.CLOSED];
    if (params?.isEnterprise && (!qParams?.statuses || qParams?.statuses?.length === 0)) qParams.statuses = [JobStatusEnum.OPEN];

    const response = await axios.get<ResponseResult<any>>(`pa/ft/job-posts/list-count`, { params: qParams });
    const res: any = response.data.data;
    const resSorted = {
        jobTitles: res?.jobTitles,
        locations: res?.locations?.sort((a, b) => {
            if (a.id === SAUDI_COUNTRY_ONLY_ID) return -1;
            if (b.id === SAUDI_COUNTRY_ONLY_ID) return 1;
            return 0;
        })
    }
    return resSorted;
})

export const getJobDetails = createAsyncThunk(`${appKey}/getJobDetails`, async (params: GetJobRequest) => {
    const response = await axios.get<ResponseResult<FullTimeJobDetails>>(`${jobDetailsUrl}/${params.jobId}`);
    return response.data.data;
})

// THIS THUNK IS PURPOSELY FOR KEEP CALLING JOB DETAILS API WITHOUT UPDATING THE STATE.
export const getJobDetailsForRecommendations = createAsyncThunk(`${appKey}/getJobDetailsForRecommendations`, async (params: GetJobRequest) => {
    const response = await axios.get<ResponseResult<FullTimeJobDetails>>(`${jobDetailsUrl}/${params.jobId}`);
    return response.data.data;
})

export const setJobIsRecommending = createAction<boolean>(`${appKey}/setJobIsRecommending`);

const clearRecommendationsPollingInterval = () => {
    if (window.CHECK_RECOMMENDATIONS_INTERVAL) {
        clearInterval(window.CHECK_RECOMMENDATIONS_INTERVAL);
        window.CHECK_RECOMMENDATIONS_INTERVAL = null;
    }
}

export const beginRecommendationsPolling = (jobId: string) => (dispatch: any, getState: any) => {
    dispatch(setIsBackendRecommending(true));
    clearRecommendationsPollingInterval();

    window.CHECK_RECOMMENDATIONS_INTERVAL = setInterval(() => {
        dispatch(getJobDetailsForRecommendations({ jobId }))
            .then((res: any) => {
                const jobRes: FullTimeJobDetails | undefined = res.payload;
                if (jobRes && !jobRes.isRecommending) {
                    clearRecommendationsPollingInterval();
                    dispatch(setJobIsRecommending(false));

                    const vettingState = getState()?.vetting;
                    const baseFilters = vettingState?.filters || getDefaultVettingFilters(jobRes);
                    const activeTab = vettingState?.activeTab || VettingTabsEnum.APPLIED;
                    const listFilters = getFiltersForVettingTab(baseFilters, activeTab);

                    dispatch(getVettingApplicantsList({ jobId, filters: listFilters }));
                }
            })
    }, 6000);
}

export const getApplicant = createAsyncThunk(`${appKey}/getApplicant`, async (applicationId: string) => {
    const response = await axios.get<ResponseResult<ApplicantListItem>>(`${jobApplicationsUrl}/${applicationId}/details`);
    return response.data.data;
})

// export const getJobSeekersCount = createAsyncThunk(`${appKey}/getJobSeekersCount`, async (filters?: { gender?: string, nationality?: string, location?: any }) => {
//     const response = await axios.get<ResponseResult<{ count: number }>>(`${jobApplicationsUrl}/job-seekers-count`, { params: filters });
//     return response.data.data;
// })

export const addNote = createAsyncThunk(`${appKey}/addNote`, async (body: { applicantId: string, note: string }) => {
    const response = await axios.put<ResponseResult<any>>(`${jobApplicationsUrl}/${body.applicantId}/note`, { note: body.note });
    return response.data.data;
})

export const deleteNote = createAsyncThunk(`${appKey}/delete`, async (applicantId: string) => {
    const response = await axios.delete<ResponseResult<any>>(`${jobApplicationsUrl}/${applicantId}/note`);
    return response.data.data;
})

export const unlockContactInfo = createAsyncThunk(`${appKey}/unlockContactInfo`, async (query: UnlockContactInfoRequest, { dispatch }) => {
    const body = {
        currentPage: query.currentPage,
        applicantRankInPage: query.applicantRankInPage,
        sortMethod: query.sortMethod
    }

    const endPoint = `${jobDetailsUrl}/${query.jobId}/unlock/${query.applicantId}`
    const response = await axios.put<ResponseResult<any>>(endPoint, body);

    if (response.status === 200) {
        dispatch(getPartnerInfo());
        dispatch(getJobDetails({ partnerId: query.partnerId, jobId: query.jobId }))
    }

    return response.data.data;
})

export const updateApplicantLastViewedAt = createAsyncThunk(`${appKey}/updateApplicantLastViewedAt`, async (query: { applicationId: string, isOffline?: boolean }, { dispatch }) => {
    const response = await axios.put<ResponseResult<any>>(`${jobApplicationsUrl}/${query.applicationId}/last-viewed?isOffline=${query.isOffline || false}`);
    return response.data.data;
})

export const getProducts = createAsyncThunk(`${appKey}/getProducts`, async (params: any) => {
    const response = await axios.get(`${getProductsUrl}?jobId=${params.jobId}&vacancies=1`);
    return response.data.data;
})

export const getJobTitles = createAsyncThunk(`${appKey}/getJobTitles`, async (searchKey: string = "") => {
    const response = await axios.get(`${jobTitleUrl}?term=${searchKey}`);
    return response.data.data;
})


// Generic Search Lookup using Typesense
export const searchTypesenseLookup = createAsyncThunk(`${appKey}/getTypesenseLookup`, async (options: TypesenseOptions) => {
    const lang = localStorage.getItem('i18nextLng')?.includes('en') ? 'en' : 'ar';
    const fieldName = options.fieldName;

    const response = await postTypesenseMultiSearchLoadBalanced({
        searches: [{
            q: options.searchKey,
            query_by: fieldName,
            highlight_full_fields: fieldName,
            collection: options.collection,
            sort_by: options?.sortBy || '',
            filter_by: options.filters || '',
            page: 1,
            per_page: options.perPage || 15,
            infix: options.infix || 'off',
            stopwords: options.collection === LookupsSearchIndexEnum.PROD_GEO ? 'location-stop-words' : undefined,
        }]
    },
    );


    if (response?.data?.results?.[0].code && response?.data?.results?.[0].code !== 200 && response?.data.results?.[0].error) {
        if (isProduction()) {
            Toast.Error({ content: t("typesense_error") });
            const errorString = JSON.stringify({ report: `error in searchTypesenseLookup`, error: response?.data.results?.[0].error, message: response?.data.results?.[0].error, partnerId: localStorage.getItem(FULLTIME_PARTNER_ID_KEY) });
            captureMessage(errorString);
        } else {
            Toast.Error({ content: response?.data.results?.[0].error });
        }
        return [];
    } else {
        if (options.mapResults === false) {
            return response.data?.results?.[0]?.hits;
        } else {
            return response.data?.results?.[0]?.hits?.map(hit => {
                return {
                    ...hit,
                    displayName: hit?.highlight?.[fieldName]?.[lang]?.value?.replace(/mark/g, 'em') || hit?.document?.[fieldName]?.[lang]?.value?.replace(/mark/g, 'em') || hit?.document?.[fieldName]?.[lang]?.replace(/mark/g, 'em'),
                    [fieldName]: hit?.document?.[fieldName],
                    objectID: hit?.document?.objectID || hit?.document?.id,
                    displayNameLocalized: {
                        ar: hit?.highlight?.[fieldName]?.['ar']?.value?.replace(/mark/g, 'em') || hit?.document?.[fieldName]?.['ar']?.value?.replace(/mark/g, 'em') || hit?.document?.[fieldName]?.['ar']?.replace(/mark/g, 'em'),
                        en: hit?.highlight?.[fieldName]?.['en']?.value?.replace(/mark/g, 'em') || hit?.document?.[fieldName]?.['en']?.value?.replace(/mark/g, 'em') || hit?.document?.[fieldName]?.['en']?.replace(/mark/g, 'em')
                    },
                }
            });
        }

    }
})

export const createJob = createAsyncThunk(
    `${appKey}/createJob`,
    async (body: NewJobPost, { dispatch }) => {
        const { deferFetchJobDetails, ...postBody } = body;
        const response = await axios.post(postJobUrl, postBody);

        if (response.status === 201 && !body.isMultiLocation && !deferFetchJobDetails) {
            const jobId = response.data.data.jobId;

            const jobDetails = await dispatch(getJobDetails({ jobId }));

            if (!body.isEnterprise) {
                if (jobDetails.meta.requestStatus === "fulfilled") {
                    dispatch(beginRecommendationsPolling(jobId));
                }
            }

            dispatch(getPartnerInfo())
        }
        if (body.isMultiLocation && response.status !== 201) {
            return { error: true };
        }
        return response.data.data;
    },
    { serializeError: serializeThunkApiError }
)

export const updateJob = createAsyncThunk(
    `${appKey}/updateJob`,
    async (body: NewJobPost, { dispatch }) => {
        const response = await axios.post(`${editJobUrl}/${body.jobId}`, body);
        const jobId = body.jobId;

        if (response.status === 201) {
            invalidateApiResponseInCache(getListApplicantsApiUrl(body.jobId), getInvalidationParamsFilters(getDefaultVettingFilters(null)));

            if (!body.isEnterprise) {
                dispatch(beginRecommendationsPolling(jobId));
            }

            dispatch(getPartnerInfo())
        }
        return response.data.data;
    },
    { serializeError: serializeThunkApiError }
)

export const closeJob = createAsyncThunk(`${appKey}/closeJob`, async (body: { id: string, partnerId?: string, closeReason: string, closeReasonComments: string }, { dispatch }) => {
    const response = await axios.put(`${closeJobUrl}${body.id}`, { closeReason: body.closeReason, closeReasonComments: body.closeReasonComments });
    return response.data.data;
})

export const reOpenJob = createAsyncThunk(`${appKey}/reOpenJob`, async (body: { id: string, partnerId?: string }, { dispatch }) => {
    const response = await axios.put(`${reOpenJobUrl}${body.id}`);
    return response.data.data;
})

export const archiveJob = createAsyncThunk(`${appKey}/archiveJob`, async (body: { id: string }, { dispatch }) => {
    const response = await axios.put(`${archiveJobUrl}${body.id}`);
    return response.data.data;
})

export const unArchiveJob = createAsyncThunk(`${appKey}/unArchiveJob`, async (body: { id: string }, { dispatch }) => {
    const response = await axios.put(`${archiveJobUrl}${body.id}`);
    return response.data.data;
})

export const passPaymentPrompt = createAsyncThunk(`${appKey}/passPaymentPrompt`, async (body: { jobId: string, reason?: string }) => {
    const response = await axios.put(`${passPaymentPromptUrl}/${body.jobId}/pass-payment-prompt`, { reason: body.reason });
    return response.data.data;
})

export const updateApplicantsNavParams = createAction<ApplicantsNavParams>(`${appKey}/updateApplicantsNavParams`);


export const getRecommendedSkills = createAsyncThunk(`${appKey}/getRecommendedSkills`, async (jobTitleId: string) => {
    const response = await axios.get<ResponseResult<any>>(`${recommendedSkillsUrl}?jobTitleId=${jobTitleId}`);
    return response.data.data;
})

export const getTypesenseObjectsByIDs = createAsyncThunk(`${appKey}/getTypesenseObjectsByIDs`, async (object: { index: LookupsSearchIndexEnum, ids: string[], perPage?: number }) => {
    const requests = [];
    const lang = localStorage.getItem('i18nextLng')?.includes('en') ? 'en' : 'ar';
    let fieldName = 'name';

    if (object.ids.length === 0) {
        return requests;
    }

    object.ids.map(id => requests.push(
        {
            q: id,
            query_by: 'objectID',
            highlight_full_fields: '',
            collection: object.index,
            sort_by: '',
            filter_by: '',
            page: 1,
            per_page: object.perPage || 15,
        }
    ));


    const response = await postTypesenseMultiSearchLoadBalanced(
        { searches: requests },
        '&limit_multi_searches=200',
    );

    if (response?.data?.results?.[0].code && response?.data?.results?.[0].code !== 200 && response?.data.results?.[0].error) {
        if (isProduction()) {
            // Toast.Error({ content: t("typesense_error") });
            const errorString = JSON.stringify({ report: `error in getTypesenseObjectsByIDs`, error: response?.data.results?.[0].error, message: response?.data.results?.[0].error, partnerId: localStorage.getItem(FULLTIME_PARTNER_ID_KEY) });
            captureMessage(errorString);
        } else {
            // Toast.Error({ content: response?.data.results?.[0].error });
        }
        return [];
    }

    return response.data?.results?.map(hitItem => {
        return {
            // ...hitItem,
            name: hitItem?.hits?.[0]?.document?.name || hitItem?.hits?.[0]?.document?.searchableText,
            searchableText: hitItem?.hits?.[0]?.document?.name || hitItem?.hits?.[0]?.document?.searchableText,
            objectID: hitItem?.hits?.[0]?.document?.objectID,
            document: hitItem?.hits?.[0]?.document
        }
    });
})


export const writeJobDescriptionWithSaud = createAsyncThunk(`${appKey}/writeJobDescriptionWithSaud`, async (body: { jobTitleId?: string; freeJobTitle?: string }): Promise<any> => {
    const payload: { jobTitleId?: string; freeJobTitle?: string } = {};
    if (body.jobTitleId) {
        payload.jobTitleId = body.jobTitleId;
    }
    if (body.freeJobTitle) {
        payload.freeJobTitle = body.freeJobTitle;
    }
    const response = await axios.post(`${writeJobDescriptionWithSaudUrl}`, payload);
    return response.data.data;
})

export const writeBulkMessageWithAi = createAsyncThunk(`${appKey}/writeBulkMessageWithAi`, async (body: any): Promise<any> => {
    const response = await axios.post(`${writeBulkMessageWithAiUrl}`, body);
    return response.data.data;
})

export const validateJobDescription = createAsyncThunk(`${appKey}/validateJobDescription`, async (body: { jobDescription: string }): Promise<any> => {
    const response = await axios.post(`${validateJobDescriptionUrl}`, { jobDescription: body.jobDescription });
    return response.data.data;
})

export const validateJobPost = createAsyncThunk(
    `${appKey}/validateJobPost`,
    async (body: { jobTitle?: string; jobDescription?: string }): Promise<any> => {
        const response = await axios.post(`${validateJobPostUrl}`, body);
        return response.data.data;
    },
    { serializeError: serializeThunkApiError },
)

export const setShowJobDetailsModal = createAction<boolean>(`${appKey}/setShowJobDetailsModal`);
export const updateJobRemainingInterviewsBalance = createAction<number>(`${appKey}/updateJobRemainingInterviewsBalance`);
export const setPendingInterviewsCount = createAction<number>(`${appKey}/setPendingInterviewsCount`);
export const setFilters = createAction<GetJobListRequest>(`${appKey}/setFilters`);
export const setIsFiltersReset = createAction<boolean>(`${appKey}/setIsFiltersReset`);
export const setSelectedFilters = createAction<GetJobListRequest>(`${appKey}/setSelectedFilters`);
export const setFilterErrors = createAction<JobsEnumObject>(`${appKey}/setFilterErrors`);
export const setIsCurrentPageResetFromFilters = createAction<boolean>(`${appKey}/setIsCurrentPageResetFromFilters`);
export const setIsFiltersMenuVisible = createAction<boolean>(`${appKey}/setIsFiltersMenuVisible`);
export const setFirstListHasLoaded = createAction<boolean>(`${appKey}/setFirstListHasLoaded`);
export const setJobFiltersCounts = createAction<JobFilterCounts>(`${appKey}/setJobFiltersCounts`);
export const setPlgJobsPage = createAction<number>(`${appKey}/setPlgJobsPage`);
export const setSaudConfiguration = createAction<SaudInterviewConfigurationsJobUpsertRequest>(`${appKey}/setSaudConfiguration`);
export const setIsWorkplaceTypeChanged = createAction<boolean>(`${appKey}/setIsWorkplaceTypeChanged`);
export const clearJobDetails = createAction(`${appKey}/clearJobDetails`);


export const defaultListJobsFiltersMicro: GetJobListRequest = { skip: 0, limit: 50, page: 1 };
export const defaultListJobsFiltersEnterprise: GetJobListRequest = { skip: 0, limit: ENTERPRISE_JOB_POSTS_PAGE_LIMIT, page: 1, isEnterprise: true, statuses: [JobStatusEnum.OPEN] };

const initialState: JobInitialState = {
    jobList: [],
    jobsCount: null,
    jobDetails: null,
    firstListLoaded: false,
    jobRemainingInterviewsBalance: 0,
    applications: [],
    totalApplicants: 0,
    isApplicationLoading: true,
    isLoading: false,
    isLoadingList: false,
    isUnlockLoading: false,
    applicantDetails: null,
    isProductListLoading: false,
    productList: [],
    lastJobDraft: null,
    matches: null,
    jobTitles: [],
    jobSeekersCount: 0,
    applicantsNavParams: {
        from: 'JobsList',
        scrollPosition: 0,
        currentPage: 0
    },
    shortListedCount: 0,
    pendingCount: 0,
    notSelectedCount: 0,
    recommendedSkills: [],
    showJobDetailsModal: false,
    pendingInterviewsCount: 0,
    plgJobsPage: 1,

    // enterprise
    isFiltersMenuVisible: window?.innerWidth <= 780 ? false : true,
    filters: {},
    selectedFilters: {},
    filterErrors: {},
    isFiltersReset: false,
    jobFiltersCounts: null,
    jobFiltersCountsFromApi: null,
    isLoadingJobFiltersCounts: false,
    firstListHasLoaded: false,
    jobsCounts: { activeCount: '-', archivedCount: '-', closedCount: '-', totalCount: '-' },
    saudConfiguration: null,
    isWorkplaceTypeChanged: false,
}
export const appFullTimeJobListSlice = createSlice({
    name: appKey,
    initialState,
    reducers: {
        handleClearApplicantsList: (state) => {
            state.applications = []
        },
        handleClearLastJobDraft: (state) => {
            state.lastJobDraft = null;
        },
        clearJobDetails: (state) => {
            state.jobDetails = null;
        }
    },
    extraReducers: builder => {
        builder
            .addCase(getJobList.pending, (state, action) => {
                state.isLoading = action?.meta?.arg?.hideLoading ? false : true;
                state.isLoadingList = true;
                state.jobDetails = null;
            })
            .addCase(getJobList.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isLoadingList = false;
                const res = action.payload;
                if (res?.list) {
                    const lang = getLang();
                    state.jobList = res.list.map((job) => ({
                        ...job,
                        jobTitleName: resolveJobTitleName(job, lang, state.jobFiltersCountsFromApi?.jobTitles) ?? job.jobTitleName,
                    })); // sorting is now handled by backend
                    state.jobsCount = res.count;
                    state.firstListLoaded = true;
                    if (res?.closedCount !== undefined && res?.closedCount !== null) {
                        state.jobsCounts = { activeCount: res?.activeCount, archivedCount: res?.archivedCount, closedCount: res?.closedCount, totalCount: res?.count };
                    }
                    // const sortedByDate = res.list.sort((a, b) => b.issueDate - a.issueDate);
                    // const jobList = [...sortedByDate];
                    // const activeJobList = jobList.filter(x => x.status === JobStatusEnum.OPEN);
                    // const nonRejectedJobList = jobList.filter(x => x.status !== JobStatusEnum.OPEN && x.status !== JobStatusEnum.REJECTED);
                    // const rejectedJobList = jobList.filter(x => x.status === JobStatusEnum.REJECTED);
                    // state.jobList = [...activeJobList, ...nonRejectedJobList, ...rejectedJobList];
                }
            })
            .addCase(getJobList.rejected, (state, action) => {
                state.isLoading = false;
                state.isLoadingList = false;
                setTimeout(() => {
                    const token = localStorage.getItem(FULLTIME_PARTNER_TOKEN_KEY);
                    if (!token) {
                        Toast.Error({ content: t("session_ended") })
                        localStorage.clear();
                        window.location.href = '/#/login';
                    }
                }, 5000);
            })
            .addCase(getJobDetails.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(getJobDetails.fulfilled, (state, action) => {
                state.isLoading = false;
                const lang = getLang();
                state.jobDetails = {
                    ...action.payload,
                    jobTitleName: resolveJobTitleName(action.payload, lang, state.jobFiltersCountsFromApi?.jobTitles) ?? action.payload.jobTitleName,
                };
                state.jobRemainingInterviewsBalance = action.payload?.remainingInterviewsBalance;
                state.pendingInterviewsCount = action.payload?.pendingInterviewsCount;
            })
            .addCase(getJobDetails.rejected, (state, action) => {
                state.isLoading = false;
                let errorMessage = action?.error?.message;
                // Pages handle these with dedicated error screens (no toast flash).
                if (errorMessage === 'Not Found' || errorMessage === 'Forbidden') return;
                if (errorMessage === 'Unauthorized') errorMessage = t('unauthorized');
                Toast.Error({ content: errorMessage })
            })
            .addCase(unlockContactInfo.pending, (state) => {
                state.isUnlockLoading = true
            })
            .addCase(unlockContactInfo.fulfilled, (state, action) => {
                state.isUnlockLoading = false;
            })
            .addCase(unlockContactInfo.rejected, (state, action) => {
                state.isUnlockLoading = false;
                // TODO: 
                if (action?.error?.code !== 'FT-011') {
                    Toast.Error({ content: action.error?.message })
                }
            })
            .addCase(getApplicant.fulfilled, (state, action) => {
                state.applicantDetails = action.payload;
            })
            .addCase(getApplicant.rejected, (state, action) => {
                let errorMessage = action?.error?.message;
                if (errorMessage === 'Unauthorized') errorMessage = t('unauthorized');
                if (errorMessage === 'Rejected') errorMessage = t('rejected_request');
                if (action?.error?.code !== "FT-014") {
                    Toast.Error({ content: errorMessage })
                }
            })
            .addCase(getProducts.pending, (state) => {
                state.isProductListLoading = true;
            })
            .addCase(getProducts.fulfilled, (state, action) => {
                state.isProductListLoading = false;
                state.productList = action.payload
            })
            .addCase(getProducts.rejected, (state, action) => {
                state.isProductListLoading = false;
                Toast.Error({ content: action.error?.message })
            })
            .addCase(getJobTitles.fulfilled, (state, action) => {
                state.jobTitles = action.payload
            })
            .addCase(createJob.fulfilled, (state, action) => {
                state.matches = action.payload;
            })
            .addCase(createJob.rejected, (state, action) => {
                let errorMessage = action?.error?.message;
                if (errorMessage === 'Unprocessable Entity') errorMessage = t('not_allowed');
                if (errorMessage === 'Job is rejected' || errorMessage === 'تم رفض الوظيفة') errorMessage = t('job_is_rejected');
                if (errorMessage === 'Invalid Request') errorMessage = t('invalid_request');
                if (errorMessage === 'Rejected') errorMessage = t('api_rejected');

                Toast.Error({ content: errorMessage });
                const errorString = JSON.stringify({ report: `error in createJob`, error: errorMessage, message: errorMessage, partnerId: localStorage.getItem(FULLTIME_PARTNER_ID_KEY) });
                captureMessage(errorString);
                if (errorMessage === t('not_allowed') || errorMessage === t('job_is_rejected') || errorMessage.includes('Unauthorized')) {
                    setTimeout(() => {
                        window.location.href = '/#/jobs';
                        window.location.reload();
                    }, 5000);
                }
            })
            .addCase(updateJob.rejected, (state, action) => {
                let errorMessage = action?.error?.message;
                if (errorMessage === 'Unprocessable Entity') errorMessage = t('not_allowed');
                if (errorMessage === 'Job is rejected' || errorMessage === 'تم رفض الوظيفة') errorMessage = t('job_is_rejected');
                if (errorMessage === 'Invalid Request') errorMessage = t('invalid_request');
                if (errorMessage === 'Rejected') errorMessage = t('api_rejected');

                Toast.Error({ content: errorMessage });
                const errorString = JSON.stringify({ report: `error in updateJob`, error: errorMessage, message: errorMessage, partnerId: localStorage.getItem(FULLTIME_PARTNER_ID_KEY) });
                captureMessage(errorString);
                if (errorMessage === t('not_allowed') || errorMessage === t('job_is_rejected') || errorMessage.includes('Unauthorized')) {
                    setTimeout(() => {
                        window.location.href = '/#/jobs';
                        window.location.reload();
                    }, 5000);
                }
            })
            .addCase(updateApplicantsNavParams, (state, action) => {
                state.applicantsNavParams = action.payload;
            })
            .addCase(setPlgJobsPage, (state, action) => {
                state.plgJobsPage = action.payload;
            })
            // .addCase(getJobSeekersCount.fulfilled, (state, action) => {
            //     state.jobSeekersCount = action.payload?.count;
            // })
            .addCase(passPaymentPrompt.fulfilled, (state, action) => {
                Toast.Success({ content: t('thank_you_for_your_answer') });
            })
            .addCase(passPaymentPrompt.rejected, (state, action) => {
                Toast.Error({ content: action?.error?.message });
            })
            .addCase(searchTypesenseLookup.rejected, (state, action) => {
                const errorString = JSON.stringify({ report: `error in searchTypesenseLookup`, error: action?.error, message: action?.error?.message, partnerId: localStorage.getItem(FULLTIME_PARTNER_ID_KEY) });
                captureMessage(errorString);
            })
            .addCase(closeJob.fulfilled, (state, action) => {
                Toast.Success({ content: t('close_job_confirmation_message') });
            })
            .addCase(closeJob.rejected, (state, action) => {
                Toast.Error({ content: action?.error?.message });
            })
            .addCase(archiveJob.fulfilled, (state, action) => {
                Toast.Success({ content: t('archive_job_confirmation_message') });
            })
            .addCase(archiveJob.rejected, (state, action) => {
                Toast.Error({ content: action?.error?.message });
            })
            .addCase(unArchiveJob.fulfilled, (state, action) => {
                Toast.Success({ content: t('unarchive_job_confirmation_message') });
            })
            .addCase(unArchiveJob.rejected, (state, action) => {
                Toast.Error({ content: action?.error?.message });
            })
            .addCase(getRecommendedSkills.rejected, (state, action) => {
                // Toast.Error({ content: action?.error?.message });
            })
            .addCase(reOpenJob.fulfilled, (state, action) => {
                Toast.Success({ content: t('reopen_job_confirmation_message') });
            })
            .addCase(reOpenJob.rejected, (state, action) => {
                Toast.Error({ content: action?.error?.message });
            })
            .addCase(setShowJobDetailsModal, (state, action) => {
                state.showJobDetailsModal = action.payload;
            })
            .addCase(writeJobDescriptionWithSaud.fulfilled, (state, action) => {
            })
            .addCase(writeJobDescriptionWithSaud.rejected, (state, action) => {
                Toast.Error({ content: action?.error?.message });
            })
            .addCase(validateJobDescription.fulfilled, (state, action) => {
            })
            .addCase(validateJobDescription.rejected, (state, action) => {
                // Toast.Error({ content: action?.error?.message });
            })
            .addCase(updateJobRemainingInterviewsBalance, (state, action) => {
                state.jobRemainingInterviewsBalance = action.payload;
            })
            .addCase(setPendingInterviewsCount, (state, action) => {
                state.pendingInterviewsCount = action.payload;
            })

            .addCase(setIsFiltersMenuVisible, (state, action) => {
                state.isFiltersMenuVisible = action.payload;
            })
            .addCase(setFilters, (state, action) => {
                state.filters = action.payload;
            })
            .addCase(setIsFiltersReset, (state, action) => {
                state.isFiltersReset = action.payload;
            })
            .addCase(setSelectedFilters, (state, action) => {
                state.selectedFilters = action.payload;
            })
            .addCase(setFilterErrors, (state, action) => {
                state.filterErrors = action.payload;
            })
            .addCase(setFirstListHasLoaded, (state, action) => {
                state.firstListHasLoaded = action.payload;
            })
            .addCase(getJobListFiltersCounts.pending, (state) => {
                state.isLoadingJobFiltersCounts = true;
            })
            .addCase(getJobListFiltersCounts.fulfilled, (state, action) => {
                state.isLoadingJobFiltersCounts = false;
                state.jobFiltersCounts = action.payload;
                state.jobFiltersCountsFromApi = action.payload;

                if (state.jobList?.length) {
                    const lang = getLang();
                    state.jobList = state.jobList.map((job) => ({
                        ...job,
                        jobTitleName: resolveJobTitleName(job, lang, action.payload?.jobTitles) ?? job.jobTitleName,
                    }));
                }

                if (state.jobDetails) {
                    const lang = getLang();
                    state.jobDetails = {
                        ...state.jobDetails,
                        jobTitleName: resolveJobTitleName(state.jobDetails, lang, action.payload?.jobTitles) ?? state.jobDetails.jobTitleName,
                    };
                }
            })
            .addCase(setJobFiltersCounts, (state, action) => {
                state.jobFiltersCounts = action.payload;
            })
            .addCase(getJobListFiltersCounts.rejected, (state, action) => {
                state.isLoadingJobFiltersCounts = false;
                Toast.Error({ content: action.error?.message })
            })
            .addCase(setSaudConfiguration, (state, action) => {
                state.saudConfiguration = action.payload;
            })
            .addCase(setIsWorkplaceTypeChanged, (state, action) => {
                state.isWorkplaceTypeChanged = action.payload;
            })
            .addCase(setJobIsRecommending, (state, action) => {
                if (state.jobDetails) {
                    state.jobDetails.isRecommending = action.payload;
                }
            })
    }
})

export const { handleClearApplicantsList, handleClearLastJobDraft } = appFullTimeJobListSlice.actions

export default appFullTimeJobListSlice.reducer
